<template>
  <div class="excel-form">
    <ContentSkeleton
      v-if="isLoading || !isDataReady"
      :variant="subform ? 'form' : 'accordion'"
      :rows="subform ? 4 : 10"
      :with-title="false"
    />
    <div v-else>
      <form
        @submit.prevent="onSubmit('submit')"
        :class="'form-' + selectedModel"
      >
        <div :class="multiple ? 'w-100' : 'row p-2'">
          <FormRenderer
            :fields="visibleFields"
            base-path=""
            :id-prefix="idPrefix || ''"
            :subform="!!subform"
            :multiple="!!multiple"
            @button-click="onButtonClick"
          />
        </div>
        <div class="col-md-12 float-left mt-3" v-if="showButtons">
          <button
            v-if="showButtons"
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting || saveInProgress"
          >
            Save
            <span
              v-if="isSubmitting || saveInProgress"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
          <button
            v-if="showButtons"
            type="button"
            class="btn btn-link"
            @click="$emit('cancel')"
          >
            Cancel
          </button>
        </div>
      </form>
      <ReferenceModal
        v-if="showReferenceModal && currentReferenceField"
        :is-visible="showReferenceModal"
        :model-name="currentReferenceField.ref"
        :model-label="currentReferenceField.label"
        :lists="lists"
        :models="models"
        :editing-item="currentEditingItem"
        @saved="handleReferenceSaved"
        @close="showReferenceModal = false"
        :parentID="getState()._id"
        :selectedModel="selectedModel"
      />
    </div>
  </div>
</template>

<script>
import { createFormEngine } from "./useFormEngine";
import FormRenderer from "./FormRenderer.vue";
import ReferenceModal from "./ReferenceModal.vue";
import permissionsMixin from "@/mixins/permissionsMixin";
import { applyCalculationRules, ruleIdToPath } from "./formCalculationRules";
import { wencesApi } from "@/api/wencesApi";
import {
  amountPathToClearOnEmptyUnitPrice,
  EMPTY_AMOUNT,
} from "@/utils/nibiruExpenseUnitPrice";
import {
  expenseRowPathForCurrencyChange,
  offerExpenseAmountConversion,
} from "@/modules/exchange_rates_module/utils/expenseCurrencyChange";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

const DEFAULT_ICONS = [
  "home", "star", "favorite", "person", "settings", "check", "close",
  "info", "warning", "help", "dashboard", "event", "email", "phone",
  "lock", "search", "shopping_cart", "chat", "camera", "notifications",
  "location_on", "folder", "delete", "add", "arrow_forward", "arrow_back",
  "cloud", "battery_full", "schedule", "volume_up", "music_note",
  "brightness_7", "language", "fingerprint", "accessibility", "edit",
  "print", "map", "flash_on", "photo_camera", "assignment", "reorder",
  "delete_forever", "restore", "storage", "security", "account_circle",
  "call", "airplanemode_active", "directions", "explore", "poll", "today",
  "group", "show_chart", "touch_app", "developer_mode", "build",
  "location_searching", "camera_alt", "people", "stars", "swap_horiz",
  "swap_vert", "visibility", "play_arrow", "pause", "skip_next",
  "skip_previous", "stop", "settings_backup_restore", "access_alarm",
  "cloud_download", "cloud_upload", "alarm", "radio_button_checked",
  "radio_button_unchecked", "check_circle", "error", "message",
  "system_update", "add_circle", "remove_circle", "remove_circle_outline",
  "check_box", "indeterminate_check_box", "highlight_off", "save",
  "content_copy", "filter_list", "clear", "open_in_new", "file_copy",
  "more_vert", "more_horiz", "favorite_border", "flash_off", "battery_alert",
  "restore_from_trash", "tune", "undo", "redo", "phone_iphone",
  "tablet_android", "keyboard_arrow_up", "keyboard_arrow_down",
  "keyboard_arrow_left", "keyboard_arrow_right", "home_work", "business",
  "school", "event_note", "explore_off", "recent_actors", "apps",
  "alarm_add", "check_circle_outline", "check_box_outline_blank",
  "watch_later", "create", "keyboard", "mouse", "insert_emoticon", "autorenew",
];

export default {
  name: "FormV2",
  /**
   * Los campos del formulario los pinta FormRenderer → FieldText (y otros).
   * Textarea con HTML (definición del modelo: type=textarea y html=true): CKEditor en
   * model-definition-core/text/editField.vue. Instalar paquetes ckeditor y tener
   * transpileDependencies en vue.config.js; no hace falta duplicar lógica aquí.
   */
  mixins: [permissionsMixin],
  components: {
    ContentSkeleton,
    FormRenderer,
    ReferenceModal,
  },
  props: {
    visualGroups: { type: [Array, Object, Boolean, null], default: () => [] },
    fields: { type: [Array, Object, Boolean, null], default: () => [] },
    modelDefinitions: { type: [Object, Array, Boolean, null], default: () => ({}) },
    subform: { type: [Boolean, Object, Array, null], default: false },
    defaultRules: { type: [Array, Object, Boolean, null] },
    value: { type: [Object, Array, Boolean, null], default: () => ({}) },
    selectedModel: { type: [String, Object, Boolean, null], default: "" },
    lists: { type: [Array, Object, Boolean, null], default: () => [] },
    models: { type: [Array, Object, Boolean, null], default: () => [] },
    showButtons: { type: [Boolean, Object, Array, null], default: true },
    /** Cuando el padre está guardando (p. ej. llamada API), deshabilita el botón Save para evitar doble envío */
    saveInProgress: { type: Boolean, default: false },
    multiple: { type: [Boolean, Object, Array, null], default: false },
    visualgrouptype: { type: [String, Object, Boolean, null], default: "fieldset" },
    model: { type: [Object, Array, Boolean, null] },
    idPrefix: { type: [String, Object, Boolean, null], default: "" },
  },
  data() {
    return {
      itemid: "",
      isSubmitting: false,
      isUploadingImages: false,
      pendingImageFiles: {},
      internalFields: [],
      open: null,
      referenceOptions: {},
      editingFields: "",
      rules: [],
      defaultIcons: DEFAULT_ICONS,
      showReferenceModal: false,
      currentReferenceField: null,
      currentEditingItem: null,
      isLoading: true,
      isDataReady: false,
      loadingPromises: [],
      isProcessingRules: false,
      _emitInputTimer: null,
      calculatedPaths: null,
    };
  },
  computed: {
    visibleFields() {
      return this.internalFields.filter((f) => !f.hide) || [];
    },
  },
  provide() {
    return { formContext: this };
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) return;
        if (this._engine) {
          this._engine.replaceState(newVal);
        }
        this.$nextTick(() => {
          this.ensureAllFields();
          this.runCalculationRules();
          this.applyVisualRules();
          this.pruneInvalidListSelections();
          // getValue() en hijos no siempre se invalida solo: forzar re-render tras cargar item
          this.$forceUpdate();
        });
      },
      deep: true,
      immediate: true,
    },
    fields: {
      handler() {
        this.transformFields();
        this.applyOpenGroupFromQuery();
      },
      immediate: true,
    },
  },
  created() {
    this.itemid = this.$route?.params?.itemId;
    this._engine = createFormEngine(this.value || {});
    this.calculatedPaths = new Set();
  },
  mounted() {
    this.initializeForm();
    this.$nextTick(() => {
      this.applyOpenGroupFromQuery();
      this.applyVisualRules();
    });
    this.$on("field-state-change", this.handleFieldStateChange);
    this.$on("field-value-change", this.handleFieldValueChange);
  },
  beforeDestroy() {
    if (this._emitInputTimer) clearTimeout(this._emitInputTimer);
    this._emitInputTimer = null;
    this.$off("field-state-change", this.handleFieldStateChange);
    this.$off("field-value-change", this.handleFieldValueChange);
  },
  methods: {
    getState() {
      return this._engine?.getState() || {};
    },
    getValue(path) {
      return this._engine?.getValue(path);
    },
    setValue(path, value) {
      // Moneda de un gasto del embarque (#397): se recuerda la anterior para ofrecer
      // convertir el importe (ver offerExpenseConversion).
      const expenseRowPath = expenseRowPathForCurrencyChange(this.selectedModel, path);
      const previousCurrency = expenseRowPath ? this._engine?.getValue(path) : undefined;
      this._engine?.setValue(path, value);
      if (expenseRowPath) this.offerExpenseConversion(expenseRowPath, previousCurrency, value);
      // Nibiru: al borrar el precio unitario de un gasto, su importe se vacía. Va acá y no
      // en una regla del modelo porque solo puede hacerse sobre la edición: ver
      // @/utils/nibiruExpenseUnitPrice.
      const amountPath = amountPathToClearOnEmptyUnitPrice(path, value);
      if (amountPath) {
        const current = this._engine?.getValue(amountPath);
        if (current !== undefined && current !== null && current !== EMPTY_AMOUNT) {
          this._engine?.setValue(amountPath, EMPTY_AMOUNT);
        }
      }
      this.pruneInvalidListSelections();
      this.$forceUpdate();
      const isEmpty = value === null || value === undefined || value === "" ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        this._flushEmit();
        return;
      }
      this.emitInput();
    },
    /**
     * Cambió la moneda de un gasto ya guardado: se pregunta si convertir el importe con el
     * tipo de cambio de la operación o mantenerlo (exchange_rates_module).
     */
    offerExpenseConversion(rowPath, previousCurrency, nextCurrency) {
      const row = this._engine?.getValue(rowPath);
      const currencyList = Array.isArray(this.lists)
        ? this.lists.find((l) => l && l.type === "currency")
        : null;
      const labelFor = (id) => {
        const found = ((currencyList && currencyList.values) || []).find((v) => String(v._id) === String(id));
        return found ? String(found.label || "") : "";
      };
      offerExpenseAmountConversion({
        shippingId: this.getState()._id,
        row: row ? { ...row } : null,
        previousCurrency,
        nextCurrency,
        labelFor,
        t: (key, params) => this.$t(key, params),
        onConvert: (amount) => this.setValue(`${rowPath}.amount`, amount),
      }).catch((error) => console.error("[FormV2] conversión del gasto:", error));
    },
    isCalculatedField(path) {
      return this.calculatedPaths && this.calculatedPaths.has(path);
    },
    getFieldId(path, prefix) {
      const pathStr = path || "";
      if (prefix) {
        const lastSegment = pathStr.split(".").pop() || pathStr;
        return `${prefix}-${lastSegment}`;
      }
      return pathStr.replace(/\./g, "-");
    },
    /** @returns {string[]|undefined} Ids permitidos, o undefined si no hay filtro activo para este campo */
    getListOptionFilterAllowedIds(targetFieldKey) {
      const models = this.models;
      let model;
      if (Array.isArray(models)) {
        model = models.find((m) => m.modelName === this.selectedModel);
      } else if (models && typeof models === "object" && this.selectedModel) {
        model = models[this.selectedModel];
      }
      const rows = (model?.listOptionMappings || []).filter(
        (r) => r && r.targetFieldKey === targetFieldKey
      );
      if (!rows.length) return undefined;
      const state = this._engine?.getState?.() || {};
      const matching = rows.filter((r) => {
        const d = state[r.driverFieldKey];
        const id = d && typeof d === "object" && !Array.isArray(d) ? d._id : d;
        return id != null && id !== "" && String(id) === String(r.driverItemId);
      });
      if (!matching.length) return undefined;
      const ids = new Set();
      matching.forEach((r) =>
        (r.allowedTargetItemIds || []).forEach((x) => ids.add(String(x)))
      );
      return Array.from(ids);
    },
    listValuesForField(fieldDef) {
      const lt = fieldDef?.listType;
      if (!lt || !this.lists?.length) return null;
      const L = Array.isArray(this.lists)
        ? this.lists.find((l) => l.type === lt)
        : null;
      return L?.values || null;
    },
    isHierarchicalListValues(values) {
      if (!Array.isArray(values) || !values.length) return false;
      const hasRoot = values.some((item) => item && !item.parent);
      const hasChild = values.some((item) => item && item.parent);
      return hasRoot && hasChild;
    },
    /** Id de la raíz (nivel 1) del ítem en una lista jerárquica; si es plana, id del ítem */
    listSelectionRootId(value, values) {
      if (!Array.isArray(values) || !values.length) return null;
      const idMap = new Map(values.map((o) => [String(o._id || o.id), o]));
      let cur =
        value && typeof value === "object" && !Array.isArray(value)
          ? value
          : idMap.get(String(value));
      if (!cur) {
        const id = value && typeof value === "object" ? value._id : value;
        return id != null && id !== "" ? String(id) : null;
      }
      const visited = new Set();
      while (cur && cur.parent) {
        const cid = String(cur._id || cur.id);
        if (visited.has(cid)) break;
        visited.add(cid);
        cur = idMap.get(String(cur.parent));
        if (!cur) break;
      }
      return String(cur._id || cur.id);
    },
    pruneInvalidListSelections() {
      if (!this._engine || !this.selectedModel) return;
      const models = this.models;
      let model;
      if (Array.isArray(models)) {
        model = models.find((m) => m.modelName === this.selectedModel);
      } else if (models && typeof models === "object") {
        model = models[this.selectedModel];
      }
      if (!model?.fields?.length) return;
      const listFields = model.fields.filter(
        (f) => f.type === "list" && f.listType && f.key
      );
      for (const tf of listFields) {
        const allowed = this.getListOptionFilterAllowedIds(tf.key);
        if (allowed === undefined) continue;
        const set = new Set(allowed.map(String));
        const cur = this._engine.getValue(tf.key);
        const values = this.listValuesForField(tf);
        const hierarchical =
          values && this.isHierarchicalListValues(values);
        const check = (v) => {
          if (hierarchical && values) {
            const rootId = this.listSelectionRootId(v, values);
            if (rootId == null || rootId === "") return false;
            return set.has(rootId);
          }
          const id = v && typeof v === "object" && !Array.isArray(v) ? v._id : v;
          if (id == null || id === "") return false;
          return set.has(String(id));
        };
        if (tf.isArray && Array.isArray(cur)) {
          const filtered = cur.filter(check);
          if (filtered.length !== cur.length) {
            this._engine.setValue(tf.key, filtered);
          }
        } else if (cur != null && cur !== "") {
          if (!check(cur)) {
            this._engine.setValue(tf.key, tf.isArray ? [] : null);
          }
        }
      }
    },
    pushArrayItem(path, newItem) {
      this._engine?.pushArrayItem(path, newItem);
      this.emitInput();
    },
    removeArrayItem(path, index) {
      if (this.selectedModel === "shipping" && this._engine) {
        const arr = this._engine.getValue(path);
        const row = Array.isArray(arr) ? arr[index] : null;
        if (row?.payments?.length > 0) return;
      }
      this._engine?.removeArrayItem(path, index);
      const arr = this._engine?.getValue(path);
      const listNowEmpty = Array.isArray(arr) && arr.length === 0;
      if (listNowEmpty) {
        this._flushEmit();
        return;
      }
      this.emitInput();
    },
    setEditingFields(v) {
      this.editingFields = v;
    },
    setOpen(key) {
      this.open = key;
    },
    runCalculationRules() {
      if (!this._engine || this.isProcessingRules || !this.rules?.length) return;
      this.isProcessingRules = true;
      this.calculatedPaths = new Set();
      try {
        this.fillEmptyRowNumberDefaults(this.internalFields, "");
        const state = this._engine.getState();
        const get = (path) => this._engine.getValue(path);
        const set = (path, value) => {
          // Siempre escribir en destinos de reglas de cálculo (incl. rutas anidadas en group).
          // Antes se bloqueaba si isEmpty && wouldFill, lo que impedía escribir en group_xxx.campo nuevo.
          this.calculatedPaths.add(path);
          this._engine.setValue(path, value);
        };
        applyCalculationRules(state, this.rules, get, set);
        this.$forceUpdate();
      } finally {
        this.isProcessingRules = false;
      }
    },
    /**
     * Un número vacío en una fila se ve con su valor por defecto (Change/USD muestra 1), pero
     * la cuenta lo leía vacío y el importe daba 0. Antes de calcular, el valor por defecto
     * pasa al modelo: lo que se ve es lo que se multiplica y lo que se guarda.
     */
    fillEmptyRowNumberDefaults(fields, basePath) {
      (fields || []).forEach((field) => {
        if (!field || !field.key || field.type !== "group") return;
        const path = basePath ? `${basePath}.${field.key}` : field.key;
        const subs = field.itemFields && field.itemFields.length ? field.itemFields : field.fields;
        if (!field.isArray) {
          this.fillEmptyRowNumberDefaults(subs, path);
          return;
        }
        const rows = this._engine.getValue(path);
        if (!Array.isArray(rows)) return;
        const withDefault = (subs || []).filter(
          (sub) =>
            sub &&
            sub.key &&
            sub.type === "number" &&
            sub.defaultValue !== undefined &&
            sub.defaultValue !== null &&
            sub.defaultValue !== ""
        );
        if (!withDefault.length) return;
        rows.forEach((row, idx) => {
          if (!row || typeof row !== "object") return;
          withDefault.forEach((sub) => {
            const cur = row[sub.key];
            if (cur === undefined || cur === null || cur === "") {
              this._engine.setValue(`${path}.${idx}.${sub.key}`, sub.defaultValue);
            }
          });
        });
      });
    },
    _flushEmit() {
      if (!this._engine) return;
      if (this._emitInputTimer) {
        clearTimeout(this._emitInputTimer);
        this._emitInputTimer = null;
      }
      this.applyVisualRules();
      this.$nextTick(() => {
        this.pruneInvalidListSelections();
        // Idem emitInput: primero las reglas, despues el snapshot que viaja al padre.
        this.runCalculationRules();
        const state = JSON.parse(JSON.stringify(this._engine.getState()));
        const emptyKeys = Object.keys(state).filter(k => {
          const v = state[k];
          return v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
        });
        const nestedEmpty = [];
        Object.keys(state).forEach(k => {
          const v = state[k];
          if (v && typeof v === "object" && !Array.isArray(v)) {
            Object.keys(v).forEach(k2 => {
              const v2 = v[k2];
              if (v2 === null || v2 === undefined || v2 === "" || (Array.isArray(v2) && v2.length === 0)) nestedEmpty.push(k + "." + k2);
            });
          }
        });
        this.$emit("input", state);
      });
    },
    emitInput() {
      if (!this._engine) return;
      this.applyVisualRules();
      if (this._emitInputTimer) clearTimeout(this._emitInputTimer);
      this._emitInputTimer = setTimeout(() => {
        this._emitInputTimer = null;
        this.$nextTick(() => {
          this.pruneInvalidListSelections();
          // Las reglas van ANTES del snapshot: si se emite primero, el padre (y con el
          // autosave) se queda con los calculados de la edicion anterior.
          this.runCalculationRules();
          const state = JSON.parse(JSON.stringify(this._engine.getState()));
          this.$emit("input", state);
        });
      }, 50);
    },
    formWidthColClass(field) {
      const w = Number(field && field.formWidth);
      if (w === 100) return "col-12";
      if (w === 50) return "col-12 col-md-6";
      if (w === 25) return "col-12 col-sm-6 col-lg-3";
      return null;
    },
    getContainerClass(field, context) {
      const multiple = context?.multiple ?? this.multiple;
      const subform = context?.subform ?? this.subform;
      let result = "";
      if (multiple) {
        return " w-100 flex-column p-2 field-" + field.key + " " + field.type;
      }
      const fw = this.formWidthColClass(field);
      if (this.getVisualGroupType(field.key) === "box") {
        result += fw ? " " + fw + " my-3 " : " col-12 my-3 ";
      } else if (!subform && field.type === "group") {
        result += fw ? " " + fw + " m-0 p-0" : " col-12 m-0 p-0";
      } else if (subform && this.visualgrouptype === "fieldset") {
        if (field.type === "group") {
          result += (fw || "w-100") + " p-2 field-" + field.key;
        } else if (fw) {
          result += fw + " p-2 field-" + field.key;
        } else {
          result += "col-12 col-md-6 p-2 field-" + field.key;
        }
      } else {
        result += fw
          ? fw + " p-2 mb-2 field-" + field.key
          : "col-12 col-md-6 col-lg-3 p-2 mb-2 field-" + field.key;
      }
      return result;
    },
    getBoxClass(fieldKey, basePath) {
      const fullPath = basePath ? basePath + "." + fieldKey : fieldKey;
      if (this.getVisualGroupType(fieldKey) === "fieldset") {
        const isOpen = this.open === fullPath || (this.open && this.open.startsWith(fullPath + "."));
        if (isOpen) {
          return "border-primary border " + (this.selectedModel || "").toLowerCase();
        }
        return "d-none";
      }
      if (this.getVisualGroupType(fieldKey) === "box") return "";
      return "border-primary border " + (this.selectedModel || "").toLowerCase();
    },
    getClassFieldset(field, basePath) {
      if (this.subform && this.visualgrouptype === "fieldset" && this.fields?.length > 1 && field.type === "group") {
        return "  border-bottom p-1  m-0  mb-1";
      }
      if (this.subform) return " border-bottom py-2 ";
      const fullPath = basePath ? basePath + "." + field.key : field.key;
      // Una línea por sección en vez de una caja por sección: con cinco grupos
      // encajonados el formulario era todo bordes y aire.
      if (this.open === fullPath) {
        return "bg-primary text-white py-1 px-2 mb-0 " + (this.selectedModel || "").toLowerCase();
      }
      return "border-bottom py-1 px-2 ";
    },
    getVisualGroupType(groupKey) {
      const groupName = (groupKey || "").replace("group_", "");
      const group = this.visualGroups?.find((g) => g.name === groupName);
      return group?.type || "";
    },
    canEditVisualGroup(field) {
      if (field?.type !== "group") return true;
      const groupName = (field.key || "").replace("group_", "");
      const group = this.visualGroups?.find((g) => g.name === groupName);
      if (!group || !group.roleRestrictions?.length) return true;
      const userRole = localStorage.getItem("role");
      if (!userRole) return false;
      if (userRole === "admin") return true;
      const restriction = group.roleRestrictions.find((r) => r.roleName === userRole);
      return restriction ? restriction.canEdit !== false : true;
    },
    isSubGroup(field) {
      return this.subform && field?.type === "group" && field?.isArray;
    },
    setDefaultOpenGroup() {
      // Deshabilitado intencionalmente:
      // no queremos abrir automáticamente la pestaña "general" (ni ninguna otra).
      // El grupo se abre por acción del usuario o por query param openGroup.
    },
    applyOpenGroupFromQuery() {
      if (this.subform) return;
      const openGroupQuery = String(this.$route?.query?.openGroup || "").trim();
      if (!openGroupQuery) return;
      const normalized = openGroupQuery.startsWith("group-")
        ? openGroupQuery.slice("group-".length)
        : openGroupQuery;
      const exists = (this.visibleFields || []).some(
        (f) => f?.type === "group" && String(f.key || "") === normalized
      );
      if (exists) {
        this.open = normalized;
      }
    },
    visibleGroupFields(field) {
      if (!field?.fields) return [];
      return field.fields.filter((f) => !f.hide);
    },
    transformFields() {
      const systemFields = ["_id", "createdAt", "updatedAt", "createdBy", "updatedBy", "editHistory", "__v", "textPlainVersion", "idModel"];
      const filter = (fields) => (fields || []).filter((f) => !systemFields.includes(f.key));
      const filtered = filter(this.fields);
      if (filtered?.length && this.visualGroups?.length) {
        const byGroup = new Map();
        this.visualGroups.forEach((g) => byGroup.set(g.name, { key: "group_" + g.name, label: g.label, type: "group", icon: g.icon, fields: [] }));
        filtered.forEach((f) => {
          if (f.visualGroup && byGroup.has(f.visualGroup)) byGroup.get(f.visualGroup).fields.push(f);
        });
        const result = [];
        this.visualGroups.forEach((g) => {
          const gf = byGroup.get(g.name);
          if (gf?.fields?.length) result.push(gf);
        });
        filtered.forEach((f) => {
          if (!f.visualGroup || !byGroup.has(f.visualGroup)) result.push(f);
        });
        this.internalFields = result;
      } else {
        this.internalFields = [...(filtered || [])];
      }
    },
    ensureAllFields() {
      const state = this._engine?.getState();
      if (!state) return;
      const isNew = !state._id;
      const isEmptyScalar = (v) =>
        v === undefined ||
        v === null ||
        v === "" ||
        (typeof v === "number" && v === 0);
      this.internalFields.forEach((field) => {
        const val = this._engine.getValue(field.key);
        if (val === undefined || val === null || (typeof val === "number" && val === 0)) {
          if (field.isArray) {
            this._engine.setValue(field.key, isNew && field.defaultValue !== undefined ? field.defaultValue : []);
          } else if (field.type === "group") {
            this._engine.setValue(field.key, {});
          } else if (field.type === "boolean") {
            this._engine.setValue(field.key, field.defaultValue === "true");
          } else {
            this._engine.setValue(field.key, isNew && field.defaultValue !== undefined ? field.defaultValue : "");
          }
        }
        // Visual groups (box): aplicar defaultValue de subcampos vacíos (p. ej. negotiation.name)
        if (
          isNew &&
          field.type === "group" &&
          !field.isArray &&
          Array.isArray(field.fields)
        ) {
          const groupPath = field.key;
          let groupVal = this._engine.getValue(groupPath);
          if (!groupVal || typeof groupVal !== "object" || Array.isArray(groupVal)) {
            groupVal = {};
            this._engine.setValue(groupPath, groupVal);
          }
          field.fields.forEach((sub) => {
            if (!sub || !sub.key) return;
            if (sub.defaultValue === undefined || sub.defaultValue === null || sub.defaultValue === "") {
              return;
            }
            const subPath = `${groupPath}.${sub.key}`;
            const cur = this._engine.getValue(subPath);
            if (!isEmptyScalar(cur)) return;
            if (sub.type === "boolean") {
              this._engine.setValue(subPath, sub.defaultValue === "true" || sub.defaultValue === true);
            } else {
              this._engine.setValue(subPath, sub.defaultValue);
            }
          });
        }
      });
    },
    createNewArrayItem(field) {
      if (field.type === "group" || (field.itemFields && field.itemFields.length > 0)) {
        const newItem = {};
        // Casi todos los grupos traen los subcampos en `fields` y un `itemFields: []`. Antes
        // ese array vacío ganaba el `||` y la fila nacía sin nada: la pantalla mostraba el
        // valor por defecto (Change/USD 1, Currency USD) pero el modelo no lo tenía, y la
        // cuenta del importe daba 0. A esas filas se les pone solo el valor por defecto
        // definido —lo que ya se ve en pantalla—, no la plantilla de abajo, que les
        // estamparía la fecha de hoy y ceros a todos los grupos de todos los tenants.
        if (!(field.itemFields && field.itemFields.length > 0)) {
          (field.fields || []).forEach((sub) => {
            if (!sub || !sub.key || sub.isArray) return;
            if (sub.defaultValue === undefined || sub.defaultValue === null || sub.defaultValue === "") return;
            newItem[sub.key] =
              sub.type === "boolean" ? sub.defaultValue === true || sub.defaultValue === "true" : sub.defaultValue;
          });
          return newItem;
        }
        field.itemFields.forEach((sub) => {
          if (sub.isArray) newItem[sub.key] = [];
          else if (sub.type === "group") newItem[sub.key] = {};
          else if (sub.type === "boolean") newItem[sub.key] = false;
          else if (sub.type === "number") newItem[sub.key] = sub.defaultValue !== undefined ? sub.defaultValue : 0;
          else if (sub.type === "date") newItem[sub.key] = sub.time ? this.formatCurrentDateTime() : this.formatCurrentDate();
          else if (sub.type === "list") newItem[sub.key] = sub.isArray ? [] : { _id: "", name: "" };
          else if (sub.type === "reference") newItem[sub.key] = { _id: "", name: "" };
          else newItem[sub.key] = sub.defaultValue !== undefined ? sub.defaultValue : "";
        });
        return newItem;
      }
      switch (field.type) {
        case "string": case "email": return field.defaultValue ?? "";
        case "number": return field.defaultValue ?? 0;
        case "boolean": return field.defaultValue ?? false;
        case "date": return field.time ? this.formatCurrentDateTime() : this.formatCurrentDate();
        case "list": return { _id: "", name: "" };
        case "reference": return { _id: "", name: "" };
        case "textlist": return field.defaultValue ?? "";
        default: return field.defaultValue ?? "";
      }
    },
    formatCurrentDate() {
      const d = new Date();
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    formatCurrentDateTime() {
      const d = new Date();
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
      const h = String(d.getHours()).padStart(2, "0"), min = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day}T${h}:${min}`;
    },
    async initializeForm() {
      try {
        this.isLoading = true;
        this.isDataReady = false;
        this.loadingPromises = [];
        this.transformFields();
        if (this.selectedModel) this.loadingPromises.push(this.loadRules());
        await Promise.all(this.loadingPromises);
        for (const field of this.internalFields) {
          if (field.type === "reference" && field.ref) {
            this.$set(this.referenceOptions, field.key, []);
          }
        }
        this.isDataReady = true;
        this.$nextTick(() => {
          if (this._engine) {
            this.runCalculationRules();
            this.applyVisualRules();
            this.pruneInvalidListSelections();
            // Emitir estado con cálculos para que el padre (this.form) tenga los valores calculados al guardar
            this.$emit("input", JSON.parse(JSON.stringify(this._engine.getState())));
          }
        });
      } catch (e) {
        console.error("Error initializing form:", e);
      } finally {
        this.isLoading = false;
      }
    },
    async loadRules() {
      if (!this.selectedModel) return;
      try {
        this.rules = this.defaultRules || (await wencesApi.get("/api/gotrading/rules/" + this.selectedModel)).data || [];
      } catch (e) {
        console.error("Error loading rules:", e);
        this.rules = [];
      }
    },
    applyVisualRules() {
      const rules = this.models?.find((m) => m.modelName === this.selectedModel)?.visualRules || [];
      const state = this._engine?.getState();
      this.internalFields.forEach((field) => {
        this.$set(field, "hide", field.hide === true);
        rules.forEach((rule) => {
          const path = rule.targetField?.includes("-") ? ruleIdToPath(rule.targetField) : rule.targetField;
          const targetValue = path ? this._engine?.getValue(path) : state?.[rule.targetField];
          let shouldShow = true;
          switch (rule.condition) {
            case "equals":
              shouldShow = targetValue?._id ? targetValue._id === rule.value : targetValue === rule.value;
              break;
            case "notEquals":
              shouldShow = targetValue?._id ? targetValue._id !== rule.value : targetValue !== rule.value;
              break;
            case "contains": shouldShow = String(targetValue).includes(rule.value); break;
            case "notContains": shouldShow = !String(targetValue).includes(rule.value); break;
            case "isEmpty": shouldShow = !targetValue || targetValue === ""; break;
            case "isNotEmpty": shouldShow = !!targetValue && targetValue !== ""; break;
            default: break;
          }
          // «require» no toca la visibilidad: el obligatorio lo exige el back al guardar.
          if (rule.action !== "require" && rule.fieldsToToggle?.includes(field.key)) {
            this.$set(field, "hide", rule.action === "show" ? !shouldShow : shouldShow);
          }
        });
      });
      this.$emit("visual-rules-update");
    },
    handleFieldStateChange(event) {
      const { fieldId, isCalculated } = event;
      const el = this.$el?.querySelector?.("#" + fieldId);
      if (el) {
        el.classList.toggle("calculated-field", isCalculated);
        el.title = isCalculated ? "Campo calculado automáticamente" : "";
      }
    },
    handleFieldValueChange(event) {
      const { fieldId, value } = event;
      if (this._engine) {
        const path = ruleIdToPath(fieldId) || fieldId;
        this._engine.setValue(path, value);
      }
      this.emitInput();
    },
    async openReferenceModal(field, editingItem) {
      this.currentReferenceField = field;
      this.currentEditingItem = editingItem || null;
      this.showReferenceModal = true;
    },
    onButtonClick(val) {
      this.$emit("button-click", val);
    },
    handleReferenceSaved(savedItem) {
      if (!this.currentReferenceField?.ref) return;
      const key = this.currentReferenceField.key;
      const opts = this.referenceOptions[key] || [];
      const idx = opts.findIndex((i) => i._id === savedItem._id);
      if (idx !== -1) this.$set(this.referenceOptions[key], idx, savedItem);
      else this.referenceOptions[key].push(savedItem);
      const state = this._engine.getState();
      if (!this.currentReferenceField.isArray) {
        this._engine.setValue(key, savedItem);
      } else {
        const arr = this._engine.getValue(key) || [];
        this._engine.setValue(key, [...arr, savedItem]);
      }
      this.emitInput();
      this.showReferenceModal = false;
    },
    onSubmit(autosave) {
      const state = this._engine?.getState();
      if (!state) return;
      if (this.isSubmitting || this.saveInProgress) return;
      this.isSubmitting = true;
      const dataToSend = JSON.parse(JSON.stringify(state));
      delete dataToSend.editHistory;
      if (!this.subform) this.$emit("submit", dataToSend, autosave);
      this.isSubmitting = false;
    },
  },
};
</script>
<style scoped>
.list {
  min-width: 130px;
}
input[readonly],
textarea[readonly],
select[readonly] {
  background-color: #f0f0f0; /* Color de fondo gris claro */
  color: #a0a0a0; /* Color de texto gris */
  border: 1px solid #dcdcdc; /* Borde gris claro */
}

/* Forzar que el popup del multiselect siempre se abra hacia abajo */
:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;

    width: 400px !important;
  
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}

/* Asegurar que la tabla y sus elementos no corten el popup */
.table-responsive {
  overflow: visible !important;
}

.table {
  overflow: visible !important;
}

.table td {
  position: relative;
  overflow: visible !important;
}

/* Asegurar que el contenedor del multiselect tenga el contexto de apilamiento correcto */
.multiselect-container {
  position: relative;
  z-index: 1;
}

.multiselect.is-active {
  z-index: 99999;
}

/* Ocultar el backdrop duplicado */
:deep(.modal-backdrop) {
  display: none;
}

.form-control {
  padding: 0 7px !important;
}

/* Estilos para tooltips */
.tooltip-icon {
  display: inline-block;
  position: relative;
}

.tooltip-icon:hover::after {
  content: attr(title);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  margin-bottom: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.tooltip-icon:hover::before {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #333;
  margin-bottom: -5px;
  z-index: 1000;
}

/* Mejorar la apariencia del icono de info */
.tooltip-icon .md-icon {
  transition: color 0.2s ease;
}

.tooltip-icon:hover .md-icon {
  color: #007bff !important;
}
</style>
<style>
@media (max-width: 767.98px) {
  .excel-form .row > [class*="col-"] {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.group-multiple .form-label {
  display: none;
}

.group-multiple .modal-dialog .form-label {
  display: block !important;
}


.add-reference,
.edit-reference {
  padding: 0 !important;
  position: absolute;
  right: 14px;
  top: -22px;
  z-index: 9;
}

.add-reference {
  right: 34px;
}



.group-multiple {
  display: block;
}

.form-control:disabled {
  opacity: 0.6;
}

input.form-control {
  background-image: none !important;
}
</style>